import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, ChevronLeft, ChevronRight } from 'lucide-react';

const MuscleSculpting = () => {
  const navigate = useNavigate();
  const [currentBenefitIndex, setCurrentBenefitIndex] = useState(0);

  const scrollToContact = () => {
    navigate('/');
    setTimeout(() => {
      const element = document.getElementById('contact');
      if (element) {
        const offset = 90;
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - offset;
        
        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });
      }
    }, 100);
  };

  const pricingOptions = [
    {
      title: 'Tone Muscle Sculpting',
      price: '$250',
      unit: 'per session'
    }
  ];

  const benefits = [
    {
      title: 'Builds & Strengthens Muscles',
      description: 'Induces powerful muscle contractions that build and strengthen targeted muscle groups beyond what\'s achievable through exercise alone.'
    },
    {
      title: 'Burns Fat Simultaneously',
      description: 'Works dual action to build muscle while reducing fat in treated areas for enhanced body contours.'
    },
    {
      title: 'Non-Invasive Treatment',
      description: 'Achieves significant muscle toning and body sculpting without surgery, needles, or recovery time.'
    },
    {
      title: 'No Downtime Required',
      description: 'Return to normal activities immediately after treatment with no restrictions or recovery period.'
    },
    {
      title: 'Targets Multiple Areas',
      description: 'Effectively treats abdomen, buttocks, thighs, arms, and calves for comprehensive body sculpting.'
    },
    {
      title: 'Enhances Athletic Performance',
      description: 'Strengthens core muscles and improves overall muscle tone to support athletic activities and fitness goals.'
    },
    {
      title: 'Improves Posture & Core Strength',
      description: 'Strengthens deep abdominal and back muscles that support proper posture and spinal alignment.'
    },
    {
      title: 'Complements Healthy Lifestyle',
      description: 'Enhances results from diet and exercise, helping you achieve fitness goals more effectively.'
    },
    {
      title: 'Quick Treatment Sessions',
      description: 'Efficient 30-minute sessions fit easily into busy schedules with lasting muscle-building results.'
    },
    {
      title: 'Visible Results',
      description: 'Most patients see noticeable improvements in muscle definition and body contours after 4-6 sessions.'
    },
    {
      title: 'FDA-Cleared Technology',
      description: 'Safe, proven electromagnetic technology with established efficacy for muscle building and toning.'
    },
    {
      title: 'Boosts Metabolism',
      description: 'Increased muscle mass elevates metabolic rate, helping burn more calories even at rest.'
    }
  ];

  return (
    <>
      <Helmet>
        <title>Muscle Sculpting - Non-Invasive Body Contouring | Century Aesthetics</title>
        <meta name="description" content="Tone electromagnetic muscle sculpting to build muscle and burn fat non-invasively in Forest Hills, NY." />
        <meta name="keywords" content="muscle sculpting, body sculpting, electromagnetic muscle building, non-invasive body contouring, tone therapy" />
        <link rel="canonical" href="https://www.centuryaesthetics.org/muscle-sculpting" />
        
        <meta property="og:title" content="Muscle Sculpting | Century Longevity & Aesthetics" />
        <meta property="og:description" content="Tone electromagnetic muscle sculpting to build muscle and burn fat non-invasively in Forest Hills, NY." />
        <meta property="og:url" content="https://www.centuryaesthetics.org/muscle-sculpting" />
      </Helmet>

      <div className="min-h-screen bg-white pt-28">

      {/* Main Content Section */}
      <section className="py-12">
        <div className="max-w-[1400px] mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="relative"
            >
              <div className="rounded-lg overflow-hidden shadow-2xl h-[500px]">
                <img 
                  src="/muscleSculpt.jpg" 
                  alt="Muscle Sculpting - Tone Body Contouring" 
                  className="w-full h-full object-cover"
                />
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-4xl font-heading font-bold text-olive-darkest mb-6">
                TONE MUSCLE SCULPTING: BUILD MUSCLE & BURN FAT NON-INVASIVELY
              </h2>
              <div className="w-20 h-1 bg-gold mb-6"></div>
              
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>
                  Tone muscle sculpting uses FDA-cleared electromagnetic technology to build muscle and reduce fat simultaneously through powerful, focused muscle contractions. This revolutionary non-invasive treatment induces thousands of supramaximal muscle contractions—far beyond what's achievable through voluntary exercise—to strengthen, tone, and define targeted muscle groups. Tone effectively treats the abdomen, buttocks, thighs, arms, and calves, delivering dramatic improvements in muscle definition and body contours without surgery, needles, or downtime.
                </p>
                <p>
                  During each 30-minute session, electromagnetic energy triggers intense muscle contractions that force muscles to adapt and grow stronger. Most patients achieve optimal results with 4-6 sessions scheduled twice weekly. Results include increased muscle mass, reduced fat layer thickness, improved muscle tone and definition, and enhanced body contours. There's no recovery time—you can return to normal activities immediately after treatment. Schedule your consultation at Century Longevity & Aesthetics to discover how Tone muscle sculpting can help you achieve your body goals.
                </p>
              </div>

              <motion.button
                whileHover={{ y: -2 }}
                whileTap={{ scale: 0.98 }}
                onClick={scrollToContact}
                className="mt-8 bg-olive-darkest text-white px-8 py-4 rounded font-medium text-lg tracking-wide hover:bg-gold hover:text-olive-darkest transition-all duration-300 shadow-lg hover:shadow-xl"
              >
                Book Now
              </motion.button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-12 bg-olive-darkest">
        <div className="max-w-[1400px] mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl font-heading font-bold text-white mb-4">Muscle Sculpting Pricing</h2>
            <div className="w-20 h-1 bg-gold mx-auto mb-6"></div>
            <div className="inline-flex items-center gap-3 bg-gold/10 border-2 border-gold px-8 py-4 rounded-lg">
              <CheckCircle className="w-6 h-6 text-gold" />
              <p className="text-white text-lg font-medium">
                Free Consultation for Muscle Sculpting Services
              </p>
            </div>
          </motion.div>

          <div className="max-w-md mx-auto">
            {pricingOptions.map((option, index) => (
              <motion.div
                key={option.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-white rounded-lg p-12 shadow-2xl text-center"
              >
                <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gold/10 flex items-center justify-center">
                  <svg 
                    className="w-12 h-12 text-gold" 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path 
                      strokeLinecap="round" 
                      strokeLinejoin="round" 
                      strokeWidth={1.5} 
                      d="M13 10V3L4 14h7v7l9-11h-7z" 
                    />
                  </svg>
                </div>
                
                <h3 className="text-2xl font-heading font-semibold text-olive-darkest mb-4 uppercase tracking-wide">
                  {option.title}
                </h3>
                <p className="text-5xl font-heading font-light text-gold mb-2">
                  {option.price}
                </p>
                <p className="text-lg font-body text-gray-600 mb-8">
                  {option.unit}
                </p>
                
                <motion.button
                  whileHover={{ y: -2 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={scrollToContact}
                  className="w-full bg-olive-darkest text-white px-8 py-4 rounded-lg font-medium text-lg tracking-wide hover:bg-gold hover:text-olive-darkest transition-all duration-300 shadow-lg"
                >
                  Book Now
                </motion.button>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-[1400px] mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl font-heading font-bold text-olive-darkest mb-4">Benefits of Tone Muscle Sculpting</h2>
            <div className="w-20 h-1 bg-gold mx-auto"></div>
          </motion.div>

          <div className="max-w-4xl mx-auto">
            {/* Carousel Container */}
            <div className="relative">
              {/* Previous Button */}
              <button
                onClick={() => setCurrentBenefitIndex((prev) => (prev === 0 ? benefits.length - 1 : prev - 1))}
                className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 sm:-translate-x-12 z-10 bg-white rounded-full p-3 shadow-lg hover:bg-gold hover:text-white transition-all duration-300"
                aria-label="Previous benefit"
              >
                <ChevronLeft className="w-6 h-6" />
              </button>

              {/* Next Button */}
              <button
                onClick={() => setCurrentBenefitIndex((prev) => (prev === benefits.length - 1 ? 0 : prev + 1))}
                className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 sm:translate-x-12 z-10 bg-white rounded-full p-3 shadow-lg hover:bg-gold hover:text-white transition-all duration-300"
                aria-label="Next benefit"
              >
                <ChevronRight className="w-6 h-6" />
              </button>

              {/* Carousel Card */}
              <AnimatePresence mode="wait">
                <motion.div
                  key={currentBenefitIndex}
                  initial={{ opacity: 0, x: 100 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -100 }}
                  transition={{ duration: 0.3 }}
                  className="bg-white rounded-lg shadow-xl p-8 sm:p-12 min-h-[300px] flex flex-col justify-center"
                >
                  <div className="flex items-start gap-6">
                    <div className="flex-shrink-0">
                      <CheckCircle className="w-16 h-16 text-gold" strokeWidth={1.5} />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-heading text-3xl font-semibold text-olive-darkest mb-4">
                        {benefits[currentBenefitIndex].title}
                      </h3>
                      <p className="text-lg text-gray-700 leading-relaxed">
                        {benefits[currentBenefitIndex].description}
                      </p>
                    </div>
                  </div>
                </motion.div>
              </AnimatePresence>

              {/* Dots Indicator */}
              <div className="flex justify-center gap-2 mt-8">
                {benefits.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentBenefitIndex(index)}
                    className={`h-2 rounded-full transition-all duration-300 ${
                      index === currentBenefitIndex 
                        ? 'w-8 bg-gold' 
                        : 'w-2 bg-gray-300 hover:bg-gold/50'
                    }`}
                    aria-label={`Go to benefit ${index + 1}`}
                  />
                ))}
              </div>
            </div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="mt-16 bg-olive-darkest text-white p-8 sm:p-10 md:p-12 rounded-xl shadow-2xl text-center"
          >
            <p className="text-base sm:text-lg leading-relaxed mb-8 text-white">
              Build muscle, burn fat, and sculpt your ideal physique with revolutionary Tone technology—no surgery, no downtime!
            </p>
            <motion.button
              whileHover={{ y: -2, scale: 1.05 }}
              whileTap={{ scale: 0.98 }}
              onClick={scrollToContact}
              className="w-full sm:w-auto bg-gold text-olive-darkest px-8 sm:px-10 md:px-12 py-3 sm:py-4 rounded-lg font-semibold text-base sm:text-lg tracking-wide hover:bg-white transition-all duration-300 shadow-lg hover:shadow-xl touch-manipulation mb-10"
            >
              Schedule Consultation
            </motion.button>

            <div className="w-24 h-0.5 bg-gold/30 mx-auto mb-8"></div>

            <h3 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold mb-4 text-white">Have Questions?</h3>
            <p className="text-base sm:text-lg text-white mb-6 sm:mb-8 max-w-2xl mx-auto leading-relaxed">
              Learn about Tone muscle sculpting, what to expect during treatment, and how it can help you achieve your body goals.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <motion.button
                whileHover={{ y: -2, scale: 1.05 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => {
                  navigate('/faq');
                  setTimeout(() => {
                    const element = document.getElementById('muscle-sculpting');
                    if (element) {
                      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }
                  }, 100);
                }}
                className="w-full sm:w-auto bg-white/10 text-white border-2 border-white px-8 sm:px-10 md:px-12 py-3 sm:py-4 rounded-lg font-semibold text-base sm:text-lg tracking-wide hover:bg-white hover:text-olive-darkest transition-all duration-300 shadow-lg hover:shadow-xl touch-manipulation"
              >
                View FAQs
              </motion.button>
              <motion.button
                whileHover={{ y: -2, scale: 1.05 }}
                whileTap={{ scale: 0.98 }}
                onClick={scrollToContact}
                className="w-full sm:w-auto bg-white/10 text-white border-2 border-gold px-8 sm:px-10 md:px-12 py-3 sm:py-4 rounded-lg font-semibold text-base sm:text-lg tracking-wide hover:bg-gold hover:text-olive-darkest transition-all duration-300 shadow-lg hover:shadow-xl touch-manipulation"
              >
                Contact Us Directly
              </motion.button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
    </>
  );
};

export default MuscleSculpting;